import {
  PHOTO_ASSIGN_MSG,
  evalTakeoff,
  keypadInput,
  prettyFormula,
  type DrawerHand,
  type Estimate,
  type EstimatePlace,
  type ItemList,
  type KeypadKey,
  type PhotoAssign,
} from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { api } from "../../api.js";
import { treeOptions, type AxesLists } from "../../components/EstimateAxes.jsx";
import { Help, guardComposingEnter } from "../../components/ui.jsx";
import { useUnitNames } from "../../lib/units.js";
import { PhIcon } from "./icons.jsx";

/* ============================================================================
   写真の引き出し（#118、docs/47 §4・§11、docs/44 §5、Figma「見積（スマホ・PC）10/3」S4 39:316・S5 39:382・S6 39:433）
   撮った写真（や「行に入れていない写真」で選んだ写真）を ①場所 → ②品目（いくつでも）→ ③採寸（任意）に振り分けて「決定」。
   右手なら右から（左に撮った写真が細く見える）、左手なら左から（個人設定）。押すところは画面の下半分、行は 56px。
   電波が無くても進められる（一覧はもう読んである分を使い、決定は送り待ちの列に積む＝画面の Workbench が enqueueAssign）
   ============================================================================ */

const fill = (s: string, v: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k: string) => String(v[k] ?? ""));
const M = PHOTO_ASSIGN_MSG;

/** 引き出しに渡す写真（送った写真は id、送り待ちの写真は clientUuid） */
export type DrawerPhoto = { key: string; src: string; id?: string | undefined; clientUuid?: string | undefined };
/** 場所の選び方：この見積の場所・会社の雛形（id）か、名前で作る */
type PlacePick = { id?: string; label: string; name: string; parentName?: string | undefined };
/** 品目の選び方：品目マスタ（id）か、その場で足す（名前・工種） */
type ItemPick = { key: string; id?: string | undefined; name: string; tradeId: string | null; unit: string };
/** 「前と同じ」（端末に覚える。見積ごと） */
export type LastAssign = { place: PlacePick; items: ItemPick[] };
const LAST_KEY = (estimateId: string) => `gb.phone.lastAssign.${estimateId}`;
export function loadLastAssign(estimateId: string): LastAssign | null {
  try {
    const v = localStorage.getItem(LAST_KEY(estimateId));
    return v ? (JSON.parse(v) as LastAssign) : null;
  } catch {
    return null;
  }
}
function saveLastAssign(estimateId: string, v: LastAssign): void {
  try {
    localStorage.setItem(LAST_KEY(estimateId), JSON.stringify(v));
  } catch {
    /* 覚えられなくても決定はできる */
  }
}

export type Decision = {
  payload: Omit<PhotoAssign, "version" | "clientUuid">;
  placeLabel: string;
  lineCount: number;
};

/** 場所の一覧（2段）：会社の雛形とこの見積の場所を名前で重ねる。この見積の場所の id があればそれ、無ければ雛形の id（決定で見積へ写す） */
type PNode = { key: string; name: string; label: string; depth: 0 | 1; estId?: string; tplId?: string; parentName?: string; count: number };
function placeNodes(estPlaces: EstimatePlace[], templates: EstimatePlace[], est: Estimate): PNode[] {
  const count = (id: string | undefined) => (id ? est.lines.filter((l) => l.placeId === id && l.role !== "group").length : 0);
  const tplTree = treeOptions(templates.filter((x) => !x.documentId));
  const estTree = treeOptions(estPlaces);
  type Top = { name: string; estId?: string; tplId?: string; kids: Array<{ name: string; estId?: string; tplId?: string }> };
  const tops: Top[] = [];
  const topOf = (name: string): Top => {
    let t = tops.find((x) => x.name === name);
    if (!t) {
      t = { name, kids: [] };
      tops.push(t);
    }
    return t;
  };
  const add = (tree: ReturnType<typeof treeOptions<EstimatePlace>>, kind: "estId" | "tplId") => {
    for (const o of tree) {
      if (o.depth === 0) topOf(o.node.name)[kind] = o.node.id;
      else {
        const parentName = o.label.split(" ＞ ")[0]!;
        const t = topOf(parentName);
        let k = t.kids.find((x) => x.name === o.node.name);
        if (!k) {
          k = { name: o.node.name };
          t.kids.push(k);
        }
        k[kind] = o.node.id;
      }
    }
  };
  add(tplTree, "tplId");
  add(estTree, "estId");
  const out: PNode[] = [];
  for (const t of tops) {
    out.push({ key: t.name, name: t.name, label: t.name, depth: 0, ...(t.estId ? { estId: t.estId } : {}), ...(t.tplId ? { tplId: t.tplId } : {}), count: count(t.estId) });
    for (const k of t.kids) {
      out.push({ key: `${t.name}\u0000${k.name}`, name: k.name, label: `${t.name} ＞ ${k.name}`, depth: 1, parentName: t.name, ...(k.estId ? { estId: k.estId } : {}), ...(k.tplId ? { tplId: k.tplId } : {}), count: count(k.estId) });
    }
  }
  return out;
}
const pickOf = (n: PNode): PlacePick => {
  const id = n.estId ?? n.tplId;
  return { ...(id ? { id } : {}), label: n.label, name: n.name, ...(n.parentName ? { parentName: n.parentName } : {}) };
};

export function PhotoDrawer({ est, lists, photos, hand, onClose, onDecide }: {
  est: Estimate;
  lists: AxesLists;
  photos: DrawerPhoto[];
  hand: DrawerHand;
  onClose: () => void;
  onDecide: (d: Decision) => void;
}) {
  const itemsQ = useQuery({ queryKey: ["items"], queryFn: () => api<ItemList>("GET", "/items"), staleTime: 30_000 });
  const [step, setStep] = useState<"place" | "items" | "measure">("place");
  const [place, setPlace] = useState<PlacePick | null>(null);
  const [sel, setSel] = useState<ItemPick[]>([]);
  const [extra, setExtra] = useState<ItemPick[]>([]);
  const [tradeFilter, setTradeFilter] = useState<string>("all");
  const [formula, setFormula] = useState("");
  const [unit, setUnit] = useState("");
  const companyUnits = useUnitNames();
  const [note, setNote] = useState("");
  const [addingPlace, setAddingPlace] = useState(false);
  const [addingItem, setAddingItem] = useState(false);

  const nodes = useMemo(() => placeNodes(lists.places, lists.templates, est), [lists.places, lists.templates, est]);
  const items = (itemsQ.data?.items ?? []).filter((x) => !x.archived);
  const liveTrades = lists.trades.filter((t) => !t.archived);
  const tradeName = (id: string | null) => (id ? (lists.trades.find((t) => t.id === id)?.name ?? "") : "");
  /** この見積の場所の id（雛形を選んだときは同じ名前の見積の場所） */
  const estPlaceId = place ? (nodes.find((n) => n.label === place.label)?.estId ?? null) : null;
  /** この場所の同じ品目の行（品目を指す行・品目を指していない同じ名前の行＝API と同じ見つけ方） */
  const linesHere = (it: ItemPick) =>
    estPlaceId ? est.lines.filter((l) => l.role === "detail" && l.placeId === estPlaceId && ((!!it.id && l.itemId === it.id) || (!l.itemId && l.name.trim() === it.name.trim()))) : [];

  // ---- 最近使った（この見積の行の 場所 × 品目。新しい行から）・前と同じ（端末に覚えた直前の決定） ----
  const last = useMemo(() => loadLastAssign(est.id), [est.id]);
  const lastLabel = last ? `${last.place.label} › ${last.items.map((i) => i.name).join("・")}` : "";
  const recent = useMemo(() => {
    const placeLabel = new Map(nodes.filter((n) => n.estId).map((n) => [n.estId!, n]));
    const out: Array<{ node: PNode; item: ItemPick }> = [];
    const seen = new Set<string>();
    for (const l of [...est.lines].sort((a, b) => b.lineNo - a.lineNo)) {
      if (l.role !== "detail" || !l.placeId || !l.itemId) continue;
      const n = placeLabel.get(l.placeId);
      if (!n) continue;
      const k = `${l.placeId}|${l.itemId}`;
      if (seen.has(k)) continue;
      seen.add(k);
      const it = items.find((x) => x.id === l.itemId);
      const pick: ItemPick = { key: l.itemId, id: l.itemId, name: it?.name ?? l.name, tradeId: it?.tradeId ?? l.tradeId ?? null, unit: it?.unit ?? l.unit };
      // 「前と同じ」に入っている 場所 × 品目 は並べない（同じものを2か所に出さない）
      if (last && last.place.label === n.label && last.items.some((i) => i.name === pick.name)) continue;
      out.push({ node: n, item: pick });
      if (out.length >= 2) break;
    }
    return out;
  }, [est.lines, nodes, items, last]);

  // 既定の単位：この場所の同じ品目の行に単位があればそれ（違う単位は入れられない）、無ければ品目の既定の単位
  const defaultUnit = (picks: ItemPick[]): string => {
    const lineUnits = new Set(picks.flatMap((p) => linesHere(p).map((l) => l.unit.trim())).filter(Boolean));
    if (lineUnits.size === 1) return [...lineUnits][0]!;
    return picks.map((p) => p.unit.trim()).find(Boolean) ?? "";
  };
  const goMeasure = (p: PlacePick, picks: ItemPick[]) => {
    setPlace(p);
    setSel(picks);
    setExtra((x) => [...x, ...picks.filter((i) => !i.id && !x.some((y) => y.name === i.name))]);
    setUnit(defaultUnit(picks));
    setStep("measure");
  };
  // 「前と同じ」「最近使った」の場所・品目を、いまの一覧の id に読み替える（名前で）
  const resolveItem = (i: ItemPick): ItemPick => {
    const hit = items.find((x) => (i.id && x.id === i.id) || x.name === i.name);
    return hit ? { key: hit.id, id: hit.id, name: hit.name, tradeId: hit.tradeId, unit: hit.unit } : { ...i, key: `new:${i.name}`, id: undefined };
  };
  const resolvePlace = (p: PlacePick): PlacePick => {
    const n = nodes.find((x) => x.label === p.label);
    return n ? pickOf(n) : { label: p.label, name: p.name, ...(p.parentName ? { parentName: p.parentName } : {}) };
  };

  // ---- 決定 ----
  const decide = (withMeasure: boolean) => {
    if (!place || !sel.length) return;
    const f = formula.trim();
    const payload: Decision["payload"] = {
      photoIds: photos.filter((p) => p.id).map((p) => p.id!),
      photoClientUuids: photos.filter((p) => !p.id && p.clientUuid).map((p) => p.clientUuid!),
      ...(place.id ? { placeId: place.id } : { newPlace: { name: place.name, ...(place.parentName ? { parentName: place.parentName } : {}) } }),
      itemIds: sel.filter((i) => i.id).map((i) => i.id!),
      newItems: sel.filter((i) => !i.id).map((i) => ({ name: i.name, tradeId: i.tradeId, ...(i.unit ? { unit: i.unit } : {}) })),
      ...(withMeasure && f ? { takeoff: { formula: f, unit, memo: note.trim() } } : {}),
      ...(withMeasure && note.trim() ? { note: note.trim() } : {}),
    };
    saveLastAssign(est.id, { place, items: sel });
    onDecide({ payload, placeLabel: place.label, lineCount: sel.length });
  };

  // ---- 閉じる：×・写真の側を押す・Esc・端へスワイプ ----
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", k);
    document.body.classList.add("ph-sheet-open");
    return () => {
      document.removeEventListener("keydown", k);
      document.body.classList.remove("ph-sheet-open");
    };
  }, [onClose]);
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const onDown = (e: RPointerEvent) => {
    swipe.current = { x: e.clientX, y: e.clientY };
  };
  const onUp = (e: RPointerEvent) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (Math.abs(dx) > 80 && Math.abs(dx) > Math.abs(dy) * 1.5 && (hand === "right" ? dx > 0 : dx < 0)) onClose();
  };

  const close = (
    <button type="button" className="ph-iconbtn" aria-label={M.close} onClick={onClose} data-testid="drawer-close">
      <PhIcon k="x" />
    </button>
  );
  const head = (title: ReactNode, sub: string, back?: () => void) => (
    <div className="ph-dhead">
      <div className="t">
        {back ? (
          <button type="button" className="ph-dback" onClick={back} data-testid="drawer-back">
            <PhIcon k="chevL" />
            <span>{title}</span>
          </button>
        ) : (
          <h2>{title}</h2>
        )}
        {sub ? <p className="sub">{sub}</p> : null}
      </div>
      {close}
    </div>
  );

  const filteredItems = [...items.map((x) => ({ key: x.id, id: x.id, name: x.name, tradeId: x.tradeId, unit: x.unit }) as ItemPick), ...extra.filter((e) => !items.some((x) => x.name === e.name))].filter(
    (x) => tradeFilter === "all" || (tradeFilter === "none" ? !x.tradeId : x.tradeId === tradeFilter),
  );
  const tradeChips = treeOptions(liveTrades).filter((o) => o.depth === 0 && [...items, ...extra].some((x) => x.tradeId === o.node.id));
  const isSel = (i: ItemPick) => sel.some((x) => x.key === i.key || x.name === i.name);
  const toggle = (i: ItemPick) => setSel((s) => (s.some((x) => x.key === i.key || x.name === i.name) ? s.filter((x) => x.key !== i.key && x.name !== i.name) : [...s, i]));

  const v = formula.trim() ? evalTakeoff(formula) : null;
  const bad = !!formula.trim() && v == null;
  /** S6 の単位のボタン（#164）：選んでいる単位・品目の既定の単位を先頭に、あとは会社の単位の一覧（設定 → 単位）の並び順。採寸は数字のキーで打つ画面なので、打つ欄は置かない */
  const unitAll = [...new Set([unit, ...sel.map((i) => i.unit), ...companyUnits].map((u) => u.trim()).filter(Boolean))];
  /** 並びきらないときは最後の「ほか…」（OS の選ぶ窓＝キーボードを出さない）で全部から選ぶ。ボタンは 7 つ分の幅（「ほか…」があれば 6 つ） */
  const units = unitAll.length > 7 ? unitAll.slice(0, 6) : unitAll;
  const unitMore = unitAll.length > 7 ? unitAll : [];

  return (
    <div className="ph-dscrim" data-hand={hand} data-testid="photo-drawer-scrim">
      <button type="button" className="ph-dside" aria-label={M.close} onClick={onClose} data-testid="drawer-side">
        {photos[0] ? <img src={photos[0].src} alt="" /> : null}
      </button>
      <aside className="ph-drawer" role="dialog" aria-modal="true" aria-label={M.pickPlace} data-testid="photo-drawer" data-step={step} onPointerDown={onDown} onPointerUp={onUp}>
        {step === "place" ? (
          <>
            {head(M.pickPlace, fill(M.photos, { n: photos.length }))}
            <div className="ph-dbody">
              <ul className="ph-dlist" data-testid="drawer-places">
                {nodes.map((n) => (
                  <li key={n.key}>
                    <button
                      type="button"
                      className={"ph-drow" + (n.depth ? " child" : "")}
                      onClick={() => {
                        setPlace(pickOf(n));
                        setStep("items");
                      }}
                      data-testid="drawer-place"
                      data-name={n.label}
                    >
                      <span className="nm">{n.depth ? n.name : n.label}</span>
                      {n.count ? <span className="cnt">{fill(M.rows, { n: n.count })}</span> : null}
                      <PhIcon k="chevR" />
                    </button>
                  </li>
                ))}
              </ul>
              {addingPlace ? (
                <NameForm
                  id="dr-place"
                  label={M.placeName}
                  help={M.placeNameHelp}
                  submit={M.addPlace}
                  testId="drawer-place"
                  onCancel={() => setAddingPlace(false)}
                  onSubmit={(text) => {
                    const [a, b] = text.split(/\s*[＞>]\s*/).map((x) => x.trim()).filter(Boolean);
                    if (!a) return;
                    const label = b ? `${a} ＞ ${b}` : a;
                    const hit = nodes.find((n) => n.label === label);
                    setPlace(hit ? pickOf(hit) : b ? { label, name: b, parentName: a } : { label, name: a });
                    setAddingPlace(false);
                    setStep("items");
                  }}
                />
              ) : (
                <button type="button" className="ph-dadd" onClick={() => setAddingPlace(true)} data-testid="drawer-add-place">
                  <PhIcon k="plus" />
                  {M.addPlace}
                </button>
              )}
            </div>
            <div className="ph-dfoot" data-notice-above="">
              {recent.length ? (
                <>
                  <b className="ph-dlabel">{M.recent}</b>
                  {recent.map((r) => (
                    <button key={`${r.node.key}|${r.item.key}`} type="button" className="ph-drecent" onClick={() => goMeasure(pickOf(r.node), [resolveItem(r.item)])} data-testid="drawer-recent">
                      {r.node.label} › {r.item.name}
                    </button>
                  ))}
                </>
              ) : null}
              {last ? (
                <button type="button" className="ph-dsame" onClick={() => goMeasure(resolvePlace(last.place), last.items.map(resolveItem))} data-testid="drawer-same">
                  <span className="k">{M.same}</span>
                  <span className="v">{lastLabel}</span>
                </button>
              ) : null}
              <button type="button" className="btn text ph-dlater" onClick={onClose} data-testid="drawer-later">
                {M.later}
              </button>
            </div>
          </>
        ) : null}

        {step === "items" && place ? (
          <>
            {head(place.label, M.pickItems, () => setStep("place"))}
            <div className="ph-dbody">
              {tradeChips.length ? (
                <div className="ph-dchips" role="group" aria-label={M.trade}>
                  <button type="button" aria-pressed={tradeFilter === "all"} onClick={() => setTradeFilter("all")} data-testid="drawer-trade">
                    {M.allTrades}
                  </button>
                  {tradeChips.map((o) => (
                    <button key={o.node.id} type="button" aria-pressed={tradeFilter === o.node.id} onClick={() => setTradeFilter(o.node.id)} data-testid="drawer-trade">
                      {o.node.name}
                    </button>
                  ))}
                  {[...items, ...extra].some((x) => !x.tradeId) ? (
                    <button type="button" aria-pressed={tradeFilter === "none"} onClick={() => setTradeFilter("none")} data-testid="drawer-trade">
                      {M.noTrade}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {!filteredItems.length && itemsQ.data ? <p className="ph-empty">{M.noItems}</p> : null}
              <ul className="ph-dlist" data-testid="drawer-items">
                {filteredItems.map((i) => {
                  const on = isSel(i);
                  const here = linesHere(i).length;
                  return (
                    <li key={i.key}>
                      <button type="button" className={"ph-drow check" + (on ? " on" : "")} role="checkbox" aria-checked={on} onClick={() => toggle(i)} data-testid="drawer-item" data-name={i.name}>
                        <span className="box" aria-hidden="true">{on ? <PhIcon k="check" /> : null}</span>
                        <span className="nm">{i.name}</span>
                        {here ? <span className="cnt">{fill(M.hasLine, { n: here })}</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {addingItem ? (
                <NewItemForm
                  trades={liveTrades.map((t) => ({ id: t.id, label: treeOptions(liveTrades).find((o) => o.node.id === t.id)?.label ?? t.name }))}
                  defaultTrade={tradeFilter !== "all" && tradeFilter !== "none" ? tradeFilter : null}
                  onCancel={() => setAddingItem(false)}
                  onSubmit={(name, tradeId) => {
                    const hit = items.find((x) => x.name === name);
                    const pick: ItemPick = hit ? { key: hit.id, id: hit.id, name: hit.name, tradeId: hit.tradeId, unit: hit.unit } : { key: `new:${name}`, name, tradeId, unit: "" };
                    if (!hit) setExtra((x) => (x.some((y) => y.name === name) ? x : [...x, pick]));
                    setSel((s) => (s.some((x) => x.name === name) ? s : [...s, pick]));
                    setAddingItem(false);
                  }}
                />
              ) : (
                <button type="button" className="ph-dadd" onClick={() => setAddingItem(true)} data-testid="drawer-add-item">
                  <PhIcon k="plus" />
                  {M.addItem}
                </button>
              )}
            </div>
            <div className="ph-dfoot" data-notice-above="">
              <button
                type="button"
                className="btn primary ph-dmain"
                disabled={!sel.length}
                onClick={() => {
                  setUnit(defaultUnit(sel));
                  setStep("measure");
                }}
                data-testid="drawer-next"
              >
                {fill(M.next, { n: sel.length })}
              </button>
              <button type="button" className="btn text" disabled={!sel.length} onClick={() => decide(false)} data-testid="drawer-decide-nomeasure">
                {M.decideNoMeasure}
              </button>
            </div>
          </>
        ) : null}

        {step === "measure" && place ? (
          <>
            {head(M.item, "", () => setStep("items"))}
            <div className="ph-dbody measure">
              <div className="ph-dtags">
                <span className="chip ai" data-testid="drawer-target">
                  {place.label} › {sel.map((i) => i.name).join("・")}
                </span>
                <span className="chip">
                  <PhIcon k="camera" />
                  {fill(M.photos, { n: photos.length })}
                </span>
              </div>
              <div className={"ph-dmeasure" + (bad ? " bad" : "")}>
                <span className="lbl">{M.measure}</span>
                <output className="num f" data-testid="drawer-formula" aria-live="polite">
                  {formula ? prettyFormula(formula).replace(/-/g, "−") : "　"}
                </output>
                {v != null ? (
                  <span className="res" data-testid="drawer-result">
                    <b className="num">＝ {v.toFixed(2)} {unit}</b>
                    <span>{fill(M.intoLines, { n: sel.length })}</span>
                  </span>
                ) : bad ? (
                  <span className="ph-err" role="alert">{M.formulaInvalid}</span>
                ) : null}
              </div>
              <div className="ph-dunits" role="group" aria-label={M.unit}>
                {units.map((u) => (
                  <button key={u} type="button" aria-pressed={unit === u} onClick={() => setUnit(unit === u ? "" : u)} data-testid="drawer-unit">
                    {u}
                  </button>
                ))}
                {unitMore.length ? (
                  <select className="ph-dunitmore" value="" aria-label={`${M.unit}（${M.unitMore}）`} onChange={(e) => e.target.value && setUnit(e.target.value)} data-testid="drawer-unit-more">
                    <option value="">{M.unitMore}</option>
                    {unitMore.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                ) : null}
              </div>
              <Keypad onKey={(k) => setFormula((f) => keypadInput(f, k))} />
              <div className="field ph-dnote">
                <input id="dr-note" className="inp" value={note} placeholder=" " maxLength={200} onChange={(e) => setNote(e.target.value)} onKeyDown={guardComposingEnter} data-testid="drawer-note" />
                <label htmlFor="dr-note">{M.note}</label>
              </div>
            </div>
            <div className="ph-dfoot" data-notice-above="">
              <button type="button" className="btn primary ph-dmain" disabled={bad} onClick={() => decide(true)} data-testid="drawer-decide">
                {M.decide}
              </button>
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}

/** 自前のテンキー（0〜9 . ＋ − × ÷・1文字消す・クリア）。56px（現場で親指で押す） */
function Keypad({ onKey }: { onKey: (k: KeypadKey) => void }) {
  const rows: Array<Array<{ k: KeypadKey; label: string; aria?: string; op?: boolean }>> = [
    [{ k: "7", label: "7" }, { k: "8", label: "8" }, { k: "9", label: "9" }, { k: "×", label: "×", aria: "かける", op: true }],
    [{ k: "4", label: "4" }, { k: "5", label: "5" }, { k: "6", label: "6" }, { k: "÷", label: "÷", aria: "わる", op: true }],
    [{ k: "1", label: "1" }, { k: "2", label: "2" }, { k: "3", label: "3" }, { k: "+", label: "＋", aria: "たす", op: true }],
    [{ k: ".", label: ".", aria: "小数点" }, { k: "0", label: "0" }, { k: "back", label: "⌫", aria: M.backspace, op: true }, { k: "−", label: "−", aria: "ひく", op: true }],
  ];
  return (
    <div className="ph-keypad" data-testid="drawer-keypad">
      {rows.flat().map((x) => (
        <button key={x.k} type="button" className={"num" + (x.op ? " op" : "")} aria-label={x.aria ?? x.label} onClick={() => onKey(x.k)} data-testid={`key-${x.k}`}>
          {x.label}
        </button>
      ))}
      <button type="button" className="clr" onClick={() => onKey("clear")} data-testid="key-clear">
        {M.clear}
      </button>
    </div>
  );
}

/** 「場所追加」：名前だけ（「2F ＞ 子供部屋」は2段） */
function NameForm({ id, label, help, submit, testId, onSubmit, onCancel }: { id: string; label: string; help?: string; submit: string; testId: string; onSubmit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState("");
  return (
    <form
      className="ph-dform"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) onSubmit(v.trim());
      }}
    >
      <div className="ph-dformrow">
        <div className="field">
          <input id={id} className="inp" value={v} placeholder=" " maxLength={101} autoFocus onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { guardComposingEnter(e); if (e.key === "Escape") onCancel(); }} data-testid={`${testId}-name`} />
          <label htmlFor={id}>{label}</label>
        </div>
        {help ? <Help label={`${label}の説明`} text={help} /> : null}
      </div>
      <button type="submit" className="btn primary" disabled={!v.trim()} data-testid={`${testId}-create`}>
        {submit}
      </button>
    </form>
  );
}

/** 「品目追加」：名前と工種だけ（既定の単位・材工区分・外注先は 設定 → 工種・場所・品目） */
function NewItemForm({ trades, defaultTrade, onSubmit, onCancel }: { trades: Array<{ id: string; label: string }>; defaultTrade: string | null; onSubmit: (name: string, tradeId: string | null) => void; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [trade, setTrade] = useState(defaultTrade ?? "");
  return (
    <form
      className="ph-dform"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) onSubmit(name.trim().slice(0, 100), trade || null);
      }}
    >
      <div className="field">
        <input id="dr-item" className="inp" value={name} placeholder=" " maxLength={100} autoFocus onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { guardComposingEnter(e); if (e.key === "Escape") onCancel(); }} data-testid="drawer-item-name" />
        <label htmlFor="dr-item">{M.itemName}</label>
      </div>
      <div className="field">
        <select id="dr-item-trade" className="inp" value={trade} onChange={(e) => setTrade(e.target.value)} data-testid="drawer-item-trade">
          <option value="">{M.noTrade}</option>
          {trades.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        <label htmlFor="dr-item-trade">{M.trade}</label>
      </div>
      <button type="submit" className="btn primary" disabled={!name.trim()} data-testid="drawer-item-create">
        {M.addItem}
      </button>
    </form>
  );
}
